import React, { useState, useRef, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';

const PopupHover = ({ content, children }) => {
    const [visible, setVisible] = useState(false);
    const [coords, setCoords] = useState({ top: 0, left: 0 });
    const triggerRef = useRef(null);
    const popupRef = useRef(null);

    useLayoutEffect(() => {
        if (visible && triggerRef.current && popupRef.current) {
            const triggerRect = triggerRef.current.getBoundingClientRect();
            const popupRect = popupRef.current.getBoundingClientRect();
            const margin = 15; // Gap between the trigger and the popup
            const viewportPadding = 10;

            // Calculate initial positions
            let top = triggerRect.top;
            let left = triggerRect.left - (popupRect.width - 50) - margin;

            // Adjust for collisions
            if (left < viewportPadding) {
                left = triggerRect.right + margin; // Flip to the right side
            } else if (left + popupRect.width > window.innerWidth - viewportPadding) {
                left = window.innerWidth - popupRect.width - viewportPadding; // Stay within viewport
            }

            if (top + popupRect.height > window.innerHeight - viewportPadding) {
                top = window.innerHeight - popupRect.height - viewportPadding; // Adjust for bottom collision
            } else if (top < viewportPadding) {
                top = viewportPadding; // Adjust for top collision
            }

            setCoords({ top, left });
        }
    }, [visible]);

    const popupStyle = {
        position: 'fixed',
        top: `${coords.top}px`,
        left: `${coords.left}px`,
        zIndex: 10000,
        pointerEvents: 'none',
        backgroundColor: '#fff',
        borderRadius: '8px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
        border: '1px solid #e0e0e0',
        padding: '5px',
        overflowY: 'auto'
    };

    return (
        <div
            ref={triggerRef}
            onMouseEnter={() => setVisible(true)}
            onMouseLeave={() => setVisible(false)}
        >
            {children}
            {visible && (
                <div ref={popupRef} style={popupStyle}>
                    {content}
                </div>
            )}
        </div>
    );
};

const ViveScoreTooltip = ({ scoreDetails, children }) => {
    const scoreCard = () => {
        return (
            <div style={styles.scoreContainer}>
                <div style={styles.scoreTittleContainer}>
                    <label style={styles.scoreTittle}>Product Score</label>
                </div>
                {scoreDetails?.map((item, index) => (
                    <div key={index} style={styles.scoreCard}>
                        <div style={styles.scoreSection}>
                            <label style={styles.scoreName}>{item.Name || 'No name available'}</label>
                            <label style={styles.score(item.Score || 0)}>
                                {item.Score || 0}/100
                            </label>
                        </div>
                        <div style={styles.scoreProgressContainer}>
                            <div style={styles.scoreProgressBar(item.Score || 0)} />
                        </div>
                        <label style={styles.scoreInformation}>{item.Information || 'No information available'}</label>
                    </div>
                ))}
            </div>
        )
    }

    return <PopupHover content={scoreCard()}>{children}</PopupHover>;
};

const styles = {
    viveScoreColumn: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    scoreContainer: {
        width: '250px',
        overflowY: 'auto',
        backgroundColor: '#ffffff',
    },
    scoreTittleContainer: {
        padding: '10px 15px',
        borderBottom: '1px solid #ccc'
    },
    scoreTittle: {
        fontSize: '16px',
        fontWeight: '500',
        color: '#000000',
    },
    scoreCard: {
        display: 'flex',
        padding: '8px 15px',
        flexDirection: 'column',
        gap: '5px',
        alignItems: 'flex-start',
    },
    scoreSection: {
        display: 'flex',
        width: '100%',
        flexDirection: 'row',
        justifyContent: 'space-between',
        lineHeight: '1.3',
    },
    scoreName: {
        fontSize: '12px',
        fontWeight: '500',
        color: '#333',
    },
    score: (score) => ({
        fontSize: '12px',
        fontWeight: '700',
        color: score >= 90 ? '#1aa836' : score >= 80 ? '#017db7' : score >= 70 ? '#f5b803' : '#2a323c',
    }),
    scoreProgressContainer: {
        width: '100%',
        height: '8px',
        backgroundColor: '#f0f0f0',
        borderRadius: '50px'
    },
    scoreProgressBar: (score) => ({
        width: `${score}%`,
        height: '100%',
        background: score >= 90
            ? 'linear-gradient(135deg, #1aa836, rgba(26, 168, 54, 0.1))'
            : score >= 80 ? 'linear-gradient(135deg, #017db7, rgba(1, 125, 183, 0.1))'
                : score >= 70 ? 'linear-gradient(135deg, #f5b803, rgba(245, 184, 3, 0.1))'
                    : 'linear-gradient(135deg, #2a323c, rgba(42, 50, 60, 0.1))',
        borderRadius: '50px'
    }),
    scoreInformation: {
        fontSize: '10px',
        color: '#6c757d',
        lineHeight: '1.3',
        textAlign: 'left'
    }
};

ViveScoreTooltip.propTypes = {
    scoreDetails: PropTypes.array.isRequired,
    children: PropTypes.node.isRequired,
};

export default ViveScoreTooltip;